class NordanSampleGalleryViewer extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;

    this.abortController = new AbortController();
    this.lightboxEnabled = this.dataset.lightboxEnabled === 'true';
    this.lightboxIndex = 0;
    this.lightboxTrigger = null;
    this.aiDisclosureTrigger = null;
    this.isLightboxOpen = false;
    this.isAiDisclosureOpen = false;
    this.pointerEventsBound = false;
    this.resetDragState();

    const signal = this.abortController.signal;
    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    document.addEventListener('keydown', (event) => this.handleKeydown(event), { signal });

    this.refreshNodes();
    this.updateLightbox(false);
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.abortController = null;
    document.documentElement.classList.remove('nordan-lightbox-open', 'nordan-ai-disclosure-open');
  }

  refreshNodes() {
    this.lightbox = this.querySelector('[data-nordan-lightbox]');
    this.lightboxTrack = this.querySelector('[data-nordan-lightbox-track]');
    this.lightboxSlides = Array.from(this.querySelectorAll('[data-nordan-lightbox-slide]'));
    this.lightboxPrevButton = this.querySelector('[data-nordan-lightbox-prev]');
    this.lightboxNextButton = this.querySelector('[data-nordan-lightbox-next]');
    this.lightboxSwipeArea = this.querySelector('[data-nordan-lightbox-swipe]');
    this.aiDisclosure = this.querySelector('[data-nordan-ai-disclosure]');
  }

  handleClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const button = target.closest('button');
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    if (button.matches('[data-nordan-ai-disclosure-close]')) {
      event.preventDefault();
      event.stopPropagation();
      this.closeAiDisclosure();
      return;
    }

    if (button.matches('[data-nordan-ai-disclosure-open]')) {
      event.preventDefault();
      event.stopPropagation();
      this.openAiDisclosure(button);
      return;
    }

    if (button.matches('[data-nordan-lightbox-close]')) {
      event.preventDefault();
      event.stopPropagation();
      this.closeLightbox();
      return;
    }

    if (button.matches('[data-nordan-lightbox-prev]')) {
      event.preventDefault();
      this.showLightbox(this.lightboxIndex - 1);
      return;
    }

    if (button.matches('[data-nordan-lightbox-next]')) {
      event.preventDefault();
      this.showLightbox(this.lightboxIndex + 1);
      return;
    }

    if (button.matches('[data-nordan-gallery-open]')) {
      event.preventDefault();
      event.stopPropagation();
      if (this.lightboxEnabled) this.openLightbox(Number(button.dataset.index), button);
    }
  }

  bindLightboxPointerEvents() {
    if (this.pointerEventsBound || !this.lightboxSwipeArea || !this.abortController) return;

    const signal = this.abortController.signal;
    this.lightboxSwipeArea.addEventListener('pointerdown', (event) => this.handlePointerDown(event), { signal });
    this.lightboxSwipeArea.addEventListener('pointermove', (event) => this.handlePointerMove(event), { signal });
    this.lightboxSwipeArea.addEventListener('pointerup', (event) => this.handlePointerEnd(event), { signal });
    this.lightboxSwipeArea.addEventListener('pointercancel', (event) => this.handlePointerEnd(event), { signal });
    this.lightboxSwipeArea.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.pointerEventsBound = true;
  }

  openLightbox(index, trigger) {
    this.refreshNodes();
    if (!this.lightbox || this.lightboxSlides.length === 0) return;

    this.bindLightboxPointerEvents();
    this.lightboxTrigger = trigger;
    this.lightbox.hidden = false;
    this.isLightboxOpen = true;
    document.documentElement.classList.add('nordan-lightbox-open');
    this.lightboxIndex = Math.max(0, Math.min(index, this.lightboxSlides.length - 1));
    this.updateLightbox(false);
  }

  closeLightbox(restoreFocus = true) {
    if (!this.lightbox || !this.isLightboxOpen) return;

    this.lightbox.hidden = true;
    this.isLightboxOpen = false;
    this.resetDragState();
    document.documentElement.classList.remove('nordan-lightbox-open');

    if (restoreFocus) this.lightboxTrigger?.focus({ preventScroll: true });
    this.lightboxTrigger = null;
  }

  openAiDisclosure(trigger) {
    this.refreshNodes();
    if (!this.aiDisclosure || this.isAiDisclosureOpen) return;

    this.aiDisclosureTrigger = trigger;
    this.aiDisclosure.hidden = false;
    this.aiDisclosure.setAttribute('aria-hidden', 'false');
    this.isAiDisclosureOpen = true;
    document.documentElement.classList.add('nordan-ai-disclosure-open');
    this.aiDisclosure.querySelector('.nordan-ai-disclosure__close')?.focus({ preventScroll: true });
  }

  closeAiDisclosure(restoreFocus = true) {
    if (!this.aiDisclosure || !this.isAiDisclosureOpen) return;

    this.aiDisclosure.hidden = true;
    this.aiDisclosure.setAttribute('aria-hidden', 'true');
    this.isAiDisclosureOpen = false;
    document.documentElement.classList.remove('nordan-ai-disclosure-open');

    if (restoreFocus) this.aiDisclosureTrigger?.focus({ preventScroll: true });
    this.aiDisclosureTrigger = null;
  }

  showLightbox(index, animate = true) {
    if (this.lightboxSlides.length === 0) return;

    this.lightboxIndex = Math.max(0, Math.min(index, this.lightboxSlides.length - 1));
    this.updateLightbox(animate);
  }

  updateLightbox(animate = true, offsetPx = 0) {
    this.lightboxSlides.forEach((slide, slideIndex) => {
      const isActive = slideIndex === this.lightboxIndex;
      slide.classList.toggle('is-active', isActive);
      slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });

    if (this.lightboxTrack) {
      this.lightboxTrack.classList.toggle('is-dragging', !animate);
      this.lightboxTrack.style.transform = `translateX(calc(-${this.lightboxIndex * 100}% + ${offsetPx}px))`;
    }

    this.setControlState(this.lightboxPrevButton, this.lightboxIndex === 0);
    this.setControlState(this.lightboxNextButton, this.lightboxIndex === this.lightboxSlides.length - 1);
  }

  setControlState(button, isHidden) {
    if (!button) return;

    button.classList.toggle('is-hidden', isHidden);
    button.setAttribute('aria-disabled', isHidden ? 'true' : 'false');
    button.tabIndex = isHidden ? -1 : 0;
  }

  handlePointerDown(event) {
    if (!this.isLightboxOpen || event.target.closest('button')) return;

    this.isPointerDown = true;
    this.isDragging = false;
    this.activePointerId = event.pointerId;
    this.dragStartX = event.clientX;
    this.dragStartY = event.clientY;
    this.dragDeltaX = 0;
    this.pointerStartWasProtected = this.isPointerInsideActiveImage(event);
    this.lightboxSwipeArea.setPointerCapture?.(event.pointerId);
  }

  handlePointerMove(event) {
    if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;

    const deltaX = event.clientX - this.dragStartX;
    const deltaY = event.clientY - this.dragStartY;
    if (!this.isDragging && Math.abs(deltaX) < 8 && Math.abs(deltaY) < 8) return;
    if (!this.isDragging && Math.abs(deltaY) > Math.abs(deltaX)) return;

    event.preventDefault();
    this.isDragging = true;
    this.dragDeltaX = this.applyEdgeResistance(deltaX);
    this.updateLightbox(false, this.dragDeltaX);
  }

  handlePointerEnd(event) {
    if (!this.isPointerDown || event.pointerId !== this.activePointerId) return;

    this.lightboxSwipeArea.releasePointerCapture?.(event.pointerId);
    const viewportWidth = this.lightboxSwipeArea?.clientWidth || window.innerWidth;
    const threshold = Math.max(window.innerWidth <= 989 ? 40 : 50, viewportWidth * 0.15);
    const totalDeltaX = event.clientX - this.dragStartX;
    const totalDeltaY = event.clientY - this.dragStartY;
    const movedDistance = Math.hypot(totalDeltaX, totalDeltaY);
    const wasDragging = this.isDragging || movedDistance > 10;
    const startedOnImage = this.pointerStartWasProtected;
    const endedOnImage = this.isPointerInsideActiveImage(event);
    const deltaX = this.dragDeltaX;

    this.resetDragState();

    if (Math.abs(deltaX) >= threshold) {
      this.showLightbox(this.lightboxIndex + (deltaX < 0 ? 1 : -1));
    } else if (!wasDragging && !startedOnImage && !endedOnImage) {
      this.closeLightbox();
    } else {
      this.updateLightbox(true);
    }
  }

  isPointerInsideActiveImage(event) {
    const image = this.lightboxSlides[this.lightboxIndex]?.querySelector('.nordan-lightbox__image');
    if (!image) return false;

    const box = image.getBoundingClientRect();
    const naturalWidth = image.naturalWidth || box.width;
    const naturalHeight = image.naturalHeight || box.height;
    if (!naturalWidth || !naturalHeight || !box.width || !box.height) return false;

    const imageRatio = naturalWidth / naturalHeight;
    const boxRatio = box.width / box.height;
    const renderedWidth = imageRatio > boxRatio ? box.width : box.height * imageRatio;
    const renderedHeight = imageRatio > boxRatio ? box.width / imageRatio : box.height;
    const left = box.left + (box.width - renderedWidth) / 2;
    const top = box.top + (box.height - renderedHeight) / 2;

    return (
      event.clientX >= left &&
      event.clientX <= left + renderedWidth &&
      event.clientY >= top &&
      event.clientY <= top + renderedHeight
    );
  }

  applyEdgeResistance(deltaX) {
    if ((this.lightboxIndex === 0 && deltaX > 0) || (this.lightboxIndex === this.lightboxSlides.length - 1 && deltaX < 0)) {
      return deltaX * 0.28;
    }

    return deltaX;
  }

  resetDragState() {
    this.isPointerDown = false;
    this.isDragging = false;
    this.activePointerId = null;
    this.dragStartX = 0;
    this.dragStartY = 0;
    this.dragDeltaX = 0;
    this.pointerStartWasProtected = false;
  }

  handleKeydown(event) {
    if (this.isAiDisclosureOpen) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeAiDisclosure();
      }
      return;
    }

    if (!this.isLightboxOpen) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.closeLightbox();
    } else if (event.key === 'ArrowLeft') {
      this.showLightbox(this.lightboxIndex - 1);
    } else if (event.key === 'ArrowRight') {
      this.showLightbox(this.lightboxIndex + 1);
    }
  }
}

if (!customElements.get('nordan-sample-gallery-viewer')) {
  customElements.define('nordan-sample-gallery-viewer', NordanSampleGalleryViewer);
}
